import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { clsx } from "clsx";
import * as Tooltip from "@radix-ui/react-tooltip";
import * as Dialog from "@radix-ui/react-dialog";
import { Tooltip as UITooltip } from "./Tooltip";
import { readCookie, writeCookie } from "../utils/cookie";
import { useResponsiveTier } from "./useResponsiveTier";

export type SidebarMode = "expanded" | "rail" | "hidden";

interface SidebarCtx {
  /** Persisted/manual layout mode. Temporary hover/focus expansion never changes this value. */
  mode: SidebarMode;
  /** Effective mode used to render sidebar content, including temporary desktop expansion. */
  displayMode: SidebarMode;
  isTemporarilyExpanded: boolean;
  hasTemporaryHold: boolean;
  setMode: (m: SidebarMode) => void;
  toggle: () => void;
  setTemporaryHover: (active: boolean) => void;
  setTemporaryFocus: (active: boolean) => void;
  setTemporaryHold: (key: string, active: boolean) => void;
  dismissTemporaryExpansion: () => void;
  isMobile: boolean;
  mobileOpen: boolean;
  setMobileOpen: (b: boolean) => void;
}

const SidebarContext = createContext<SidebarCtx | null>(null);
const COOKIE = "shell.sidebar";

const isSidebarMode = (v: string): v is SidebarMode => v === "expanded" || v === "rail" || v === "hidden";

// The navigation starts as a compact rail on desktop and tablet when the user has not made an
// explicit choice. Mobile ignores `mode` entirely and continues to render as a drawer below.
const DEFAULT_MODE: SidebarMode = "rail";
// Stay open beyond the 200ms width transition so the expanding edge can catch a diagonal pointer.
const HOVER_CLOSE_GRACE_MS = 240;

type TemporaryIntent = {
  hover: boolean;
  focus: boolean;
};

export function SidebarProvider({ children }: { children: ReactNode }) {
  const { tier, isMobile } = useResponsiveTier();

  // `override` is a SNAPSHOT of the cookie taken once at mount — it holds ONLY an explicit user
  // choice (written exclusively by setMode/toggle below), never the default. `mode` is derived
  // from that explicit choice and otherwise remains the compact rail at desktop/tablet widths.
  //
  // Correctness note: an earlier version routed the tier-default through the same cookie-backed
  // setter the toggle uses, which unconditionally persists on every call. That auto-wrote a
  // cookie indistinguishable from a real choice on first mount. Keeping the default separate from
  // `override` ensures only a user click or keyboard toggle is persisted.
  const [override, setOverride] = useState<SidebarMode | null>(() => {
    const stored = readCookie(COOKIE);
    return stored && isSidebarMode(stored) ? stored : null;
  });
  const mode: SidebarMode = override ?? DEFAULT_MODE;

  const [temporaryIntent, setTemporaryIntent] = useState<TemporaryIntent>({
    hover: false,
    focus: false,
  });
  const [temporaryHolds, setTemporaryHolds] = useState<ReadonlySet<string>>(() => new Set());
  const [temporarySuppressed, setTemporarySuppressed] = useState(false);

  const setTemporarySource = useCallback((source: keyof TemporaryIntent, active: boolean) => {
    setTemporaryIntent((current) => (
      current[source] === active ? current : { ...current, [source]: active }
    ));
    // `true` is emitted only for a fresh pointer/focus entry. It intentionally releases an
    // Escape/manual-collapse latch; moving between descendants does not call this path again.
    if (active) setTemporarySuppressed(false);
  }, []);

  const setTemporaryHover = useCallback(
    (active: boolean) => setTemporarySource("hover", active),
    [setTemporarySource],
  );
  const setTemporaryFocus = useCallback(
    (active: boolean) => setTemporarySource("focus", active),
    [setTemporarySource],
  );
  const setTemporaryHold = useCallback((key: string, active: boolean) => {
    setTemporaryHolds((current) => {
      const next = new Set(current);
      if (active) next.add(key);
      else next.delete(key);
      if (next.size === current.size && next.has(key) === current.has(key)) return current;
      return next;
    });
  }, []);
  const dismissTemporaryExpansion = useCallback(() => {
    // Expanded-only descendants can unmount before the browser emits blur. Clear both sources so
    // a later fresh pointer entry cannot inherit stale focus and become impossible to dismiss.
    setTemporaryIntent({ hover: false, focus: false });
    setTemporarySuppressed(true);
  }, []);

  const hasTemporaryHold = temporaryHolds.size > 0;
  const temporaryRequested = temporaryIntent.hover || temporaryIntent.focus || hasTemporaryHold;
  const isTemporarilyExpanded =
    mode === "rail" && !isMobile && !temporarySuppressed && temporaryRequested;
  const displayMode: SidebarMode = isTemporarilyExpanded ? "expanded" : mode;

  const setMode = useCallback((next: SidebarMode) => {
    // Collapsing under a parked pointer/focused control must visibly collapse. A new pointer or
    // focus entry releases this latch; transient interaction never writes the preference cookie.
    if (next === "rail") setTemporaryIntent({ hover: false, focus: false });
    setTemporarySuppressed(next === "rail");
    writeCookie(COOKIE, next, { sameSite: "Lax", path: "/" });
    setOverride(next);
  }, []);

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // Any open popover anchored to the sidebar (e.g. the workspace switcher) is anchored to a
    // DOM node that may reflow/hide on a tier crossing — mobileOpen only matters for isMobile,
    // but closing it here too is a harmless no-op guard against a stale open drawer post-resize.
    setMobileOpen(false);
    setTemporaryIntent({ hover: false, focus: false });
    setTemporaryHolds(new Set());
    setTemporarySuppressed(false);
  }, [tier]);

  const toggle = useCallback(() => {
    if (isMobile) {
      setMobileOpen((o) => !o);
      return;
    }
    setMode(mode === "expanded" ? "rail" : "expanded");
  }, [isMobile, mode, setMode]);

  // Global ⌘B / Ctrl+B toggle — makes the "(⌘B)" affordance labels real.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "b" || e.key === "B")) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle]);

  useEffect(() => {
    if (typeof window === "undefined" || !isTemporarilyExpanded || hasTemporaryHold) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismissTemporaryExpansion();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dismissTemporaryExpansion, hasTemporaryHold, isTemporarilyExpanded]);

  const value = useMemo<SidebarCtx>(
    () => ({
      mode,
      displayMode,
      isTemporarilyExpanded,
      hasTemporaryHold,
      setMode,
      toggle,
      setTemporaryHover,
      setTemporaryFocus,
      setTemporaryHold,
      dismissTemporaryExpansion,
      isMobile,
      mobileOpen,
      setMobileOpen,
    }),
    [
      mode,
      displayMode,
      isTemporarilyExpanded,
      hasTemporaryHold,
      setMode,
      toggle,
      setTemporaryHover,
      setTemporaryFocus,
      setTemporaryHold,
      dismissTemporaryExpansion,
      isMobile,
      mobileOpen,
    ],
  );

  return (
    <SidebarContext.Provider value={value}>
      <Tooltip.Provider delayDuration={300}>{children}</Tooltip.Provider>
    </SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarCtx {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar must be used within <SidebarProvider>");
  return ctx;
}

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

export function Sidebar({ className, children, ...props }: SidebarProps) {
  const {
    mode,
    displayMode,
    isTemporarilyExpanded,
    setTemporaryHover,
    setTemporaryFocus,
    isMobile,
    mobileOpen,
    setMobileOpen,
  } = useSidebar();
  const hoverCloseTimerRef = useRef<number | null>(null);

  const cancelHoverClose = useCallback(() => {
    if (hoverCloseTimerRef.current === null) return;
    window.clearTimeout(hoverCloseTimerRef.current);
    hoverCloseTimerRef.current = null;
  }, []);

  useEffect(() => () => {
    cancelHoverClose();
    setTemporaryHover(false);
    setTemporaryFocus(false);
  }, [cancelHoverClose, setTemporaryFocus, setTemporaryHover]);

  const isPanelDomEvent = (currentTarget: HTMLDivElement, target: EventTarget) =>
    target instanceof Node && currentTarget.contains(target);

  if (isMobile) {
    return (
      <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
          <Dialog.Content className="fixed left-0 top-0 z-50 h-full w-72 bg-[var(--color-surface-elevated)] border-r border-[var(--color-border-subtle)] shadow-[var(--shadow-lg)] flex flex-col">
            <Dialog.Title className="sr-only">Navigation</Dialog.Title>
            {children}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  if (mode === "hidden") return null;

  return (
    <aside
      data-mode={mode}
      data-display-mode={displayMode}
      data-temporary-expanded={isTemporarilyExpanded ? "true" : "false"}
      className={clsx(
        // This flex slot owns only the persisted width. During temporary expansion it remains
        // 48px while the positioned panel overlays the canvas, so main content never reflows.
        "relative h-full flex-shrink-0 overflow-visible bg-[var(--surface-sunken)] transition-[width] duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)]",
        mode === "expanded" ? "w-60" : "w-12",
        isTemporarilyExpanded && "z-[var(--z-nav)]",
        className,
      )}
      {...props}
    >
      <div
        data-sidebar-panel
        onPointerEnter={(event) => {
          // React events from a portaled sidebar popover follow the React tree; accept only events
          // physically inside this panel. Touch and pen retain their normal tap/drawer behavior.
          if (
            !isPanelDomEvent(event.currentTarget, event.target)
            || (event.pointerType && event.pointerType !== "mouse")
          ) return;
          cancelHoverClose();
          setTemporaryHover(true);
        }}
        onPointerLeave={(event) => {
          if (
            !isPanelDomEvent(event.currentTarget, event.target)
            || (event.pointerType && event.pointerType !== "mouse")
          ) return;
          cancelHoverClose();
          // Let the animating edge catch a diagonally moving pointer. Re-entering anywhere in the
          // complete expanded panel cancels this close and prevents rail-to-panel flicker.
          hoverCloseTimerRef.current = window.setTimeout(() => {
            hoverCloseTimerRef.current = null;
            setTemporaryHover(false);
          }, HOVER_CLOSE_GRACE_MS);
        }}
        onFocusCapture={(event) => {
          if (!isPanelDomEvent(event.currentTarget, event.target)) return;
          const previous = event.relatedTarget;
          if (!previous || !(previous instanceof Node) || !event.currentTarget.contains(previous)) {
            setTemporaryFocus(true);
          }
        }}
        onBlurCapture={(event) => {
          if (!isPanelDomEvent(event.currentTarget, event.target)) return;
          const next = event.relatedTarget;
          if (!next || !(next instanceof Node) || !event.currentTarget.contains(next)) {
            setTemporaryFocus(false);
          }
        }}
        className={clsx(
          // One stable visual panel avoids duplicate navigation trees. The 200ms design token is
          // automatically reduced to 0ms by the design system's reduced-motion media query.
          "absolute inset-y-0 left-0 flex h-full flex-col overflow-hidden border-r border-[var(--color-border-default)] bg-[var(--surface-sunken)] transition-[width] duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)]",
          displayMode === "expanded" ? "w-60" : "w-12",
          isTemporarilyExpanded && "z-[var(--z-nav)] shadow-[var(--shadow-md)]",
        )}
      >
        {children}
      </div>
    </aside>
  );
}

export function SidebarHeader({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={clsx("flex-shrink-0 h-14 px-2 border-b border-[var(--color-border-default)] flex items-center", className)} {...props}>
      {children}
    </div>
  );
}

export function SidebarBody({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={clsx("flex-1 overflow-y-auto pb-2", className)} {...props}>
      {children}
    </div>
  );
}

export function SidebarFooter({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={clsx("flex-shrink-0 py-2 border-t border-[var(--color-border-default)] space-y-1", className)} {...props}>
      {children}
    </div>
  );
}

export function SidebarSectionLabel({ children }: { children: ReactNode }) {
  const { displayMode, isMobile } = useSidebar();
  if (displayMode === "rail" && !isMobile) return null;
  return (
    <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
      {children}
    </p>
  );
}

export interface SidebarItemProps {
  icon?: ReactNode;
  label: string;
  // Optional trailing element (e.g., status badge) shown on the right; hidden in rail mode.
  trailing?: ReactNode;
  active?: boolean;
  onClick?: () => void;
  href?: string;
}

export function SidebarItem({ icon, label, trailing, active, onClick }: SidebarItemProps) {
  const { displayMode, isMobile, setMobileOpen } = useSidebar();
  const collapsed = displayMode === "rail" && !isMobile;

  const handle = () => {
    onClick?.();
    if (isMobile) setMobileOpen(false);
  };

  const button = (
    <button
      type="button"
      onClick={handle}
      aria-label={collapsed ? label : undefined}
      aria-current={active ? "page" : undefined}
      className={clsx(
        // Active = 3px orange left bar (flush, square-left) + primary text + canvas bg.
        // Inactive carries a transparent bar so the row never shifts on selection.
        // Full-bleed rectangular rows so the orange bar sits flush at the sidebar's left edge.
        "flex items-center gap-3 w-full rounded-none border-l-[3px] text-sm transition-colors",
        collapsed ? "h-9 justify-center px-0 border-l-0" : "h-9 px-3",
        active
          ? "border-[var(--color-accent)] bg-[var(--color-surface-base)] text-[var(--color-text-primary)] font-medium"
          : "border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-subtle)]",
      )}
    >
      {icon && <span className="text-base shrink-0">{icon}</span>}
      {!collapsed && <span className="flex-1 truncate text-left">{label}</span>}
      {!collapsed && trailing && <span className="shrink-0">{trailing}</span>}
    </button>
  );

  // Keep the wrapper stable as focus expansion changes the rendered label. Tooltip suppresses
  // only its portal while disabled, so the focused trigger is never remounted.
  return (
    <UITooltip content={label} side="right" disabled={!collapsed}>
      {button}
    </UITooltip>
  );
}

// Trigger button for toggling the sidebar (rendered in TopBar typically).
export function SidebarTrigger({ className }: { className?: string }) {
  const { toggle, mode, isMobile } = useSidebar();
  const label = isMobile ? "Open navigation" : mode === "expanded" ? "Collapse sidebar" : "Expand sidebar";
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={`${label} (⌘B)`}
      className={clsx(
        // 44px hit-area (row 5.5, WCAG 2.5.5) via padding, not icon inflation — the 16px glyph
        // is unchanged; only the (transparent-at-rest) hoverable/tappable box grows.
        "h-11 w-11 inline-flex items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text-primary)]",
        className,
      )}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" stroke="currentColor" />
        <line x1="6" y1="3" x2="6" y2="13" stroke="currentColor" />
      </svg>
    </button>
  );
}
